import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, InputField } from "components/atoms";
import { ArrowDownIcon, SearchIcon } from "assets/icons";
import "./SearchBar.scss";

const SearchBarSchema = Yup.object().shape({
  searchField: Yup.string()
    .min(3)
    .required("Text must be more than 3 characters."),
});

export interface SearchBarProps {
  id: string;
  initialValue?: string;
  placeholder?: string;
  showAdvancedOptions?: boolean;
  clearForm?: boolean;
  onSearch?: (query: string) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  id,
  initialValue = "",
  placeholder = "Search",
  showAdvancedOptions = false,
  onSearch = () => {},
}) => {
  const [showOptions, setShowOptions] = useState(false);
  const [currentQuery, setCurrentQuery] = useState(initialValue);

  useEffect(() => {
    setCurrentQuery(initialValue);
    searchBarFormik.setFieldValue("searchField", initialValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  const idInputClass = `${id}_${"searchField"}`;
  const idFormClass = `${id}-${"searchBar-form"}`;

  const handleSearchBarSubmit = (values: { searchField: string }) => {
    try {
      const trimmedQuery = values.searchField.trim();
      setCurrentQuery(trimmedQuery);
      onSearch(trimmedQuery);
    } catch (error) {
      console.log(currentQuery);
      console.error("Error submitting form:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    searchBarFormik.handleChange(e);

    if (value === "") {
      setCurrentQuery("");
      onSearch("");
    } else {
      setCurrentQuery(value);
    }
  };

  const searchBarFormik = useFormik({
    initialValues: {
      searchField: initialValue,
    },
    validationSchema: SearchBarSchema,
    onSubmit: handleSearchBarSubmit,
    enableReinitialize: true,
  });

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  return (
    <form
      className="search-bar"
      id={idFormClass}
      onSubmit={searchBarFormik.handleSubmit}
      data-testid="search-bar"
    >
      {showAdvancedOptions && (
        <div className="dropdown">
          <Button
            type="button"
            text={<ArrowDownIcon />}
            className="btn-primary search-option-menu"
            onClick={toggleOptions}
            dataToggle="dropdown"
            ariaExpanded={false}
            width="2.5rem"
          />
          <div className="dropdown-menu">
            <p>Advanced options...</p>
          </div>
        </div>
      )}

      <InputField
        className="search-field"
        type="search"
        id={idInputClass}
        name="searchField"
        placeholder={placeholder}
        autoComplete="off"
        value={searchBarFormik.values.searchField}
        onChange={handleInputChange}
      />

      <Button
        type="submit"
        text={<SearchIcon />}
        className="btn-primary search-button"
        width="2.5rem"
      />
    </form>
  );
};

export default SearchBar;
